import { useState } from "react";
import { Loader2, Send, Phone, Mail, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow, MaskedLine } from "../components/Reveal";
import { submitEnquiry } from "../lib/api";
import { COMPANY, PRODUCTS, IMAGES } from "../data/products";

const PROJECT_TYPES = ["Residential Villa", "Apartment / Flat", "Commercial Space", "Renovation / Retrofit", "Other"];
const PRODUCT_OPTIONS = ["General Enquiry", ...PRODUCTS.map((p) => p.name)];

const inputCls =
  "w-full rounded-lg border border-border bg-surface px-4 py-3.5 text-sm text-slate-200 placeholder:text-slate-600 outline-none transition-colors focus:border-gold/60 focus:ring-1 focus:ring-gold/40";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: PROJECT_TYPES[0], product_interest: PRODUCT_OPTIONS[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitEnquiry(form);
      toast.success("Enquiry sent! Our team will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", project_type: PROJECT_TYPES[0], product_interest: PRODUCT_OPTIONS[0], message: "" });
    } catch (err) {
      toast.error("Could not send your enquiry. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="grain-overlay relative flex min-h-[50vh] items-end overflow-hidden" data-testid="contact-hero">
        <img src="/images/contact-hero.webp" alt="SOHOM glass skylight lounge installation" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/65 to-obsidian/30" />
        <div className="relative mx-auto w-full max-w-7xl px-6 pb-20 pt-40 sm:px-10">
          <Eyebrow className="mb-6">Contact &amp; Enquiry</Eyebrow>
          <h1 className="max-w-3xl font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>Tell us about</MaskedLine>
            <MaskedLine delay={0.32} className="text-gold">your openings.</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16 sm:px-10 lg:py-24">
        <div className="grid gap-14 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="rounded-2xl border border-border bg-carddark p-8 sm:p-10" data-testid="contact-form">
              <h2 className="font-heading text-xl font-bold text-slate-100 sm:text-2xl">Project Enquiry Form</h2>
              <p className="mt-2 text-sm text-slate-400">Fields marked required help us quote accurately. We reply within 24 hours.</p>

              <div className="mt-8 space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Name *</label>
                    <input id="contact-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} data-testid="enquiry-form-name-input" />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Phone *</label>
                    <input id="contact-phone" required minLength={7} value={form.phone} onChange={set("phone")} placeholder="+91 …" className={inputCls} data-testid="enquiry-form-phone-input" />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Email *</label>
                  <input id="contact-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} data-testid="enquiry-form-email-input" />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-project-type" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Project Type</label>
                    <select id="contact-project-type" value={form.project_type} onChange={set("project_type")} className={inputCls} data-testid="enquiry-form-project-type-select">
                      {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="contact-product" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Product Interest</label>
                    <select id="contact-product" value={form.product_interest} onChange={set("product_interest")} className={inputCls} data-testid="enquiry-form-product-select">
                      {PRODUCT_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Message *</label>
                  <textarea id="contact-message" required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder="Sizes, quantities, location, timelines — anything that helps." className={inputCls} data-testid="enquiry-form-message-input" />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 font-semibold text-obsidian transition-colors duration-300 hover:bg-gold-hover disabled:opacity-60"
                  data-testid="contact-submit-button"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {sending ? "Sending…" : "Send Enquiry"}
                </button>
              </div>
            </form>
          </Reveal>

          <div className="space-y-6 lg:col-span-2">
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-border bg-carddark p-8" data-testid="contact-details-card">
                <h3 className="font-heading text-lg font-bold text-slate-100">Reach Us Directly</h3>
                <ul className="mt-6 space-y-5 text-sm">
                  <li className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gold/30 bg-gold/10"><Phone className="h-4 w-4 text-gold" /></span>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Sales &amp; Quotes</p>
                      <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} className="mt-1 block text-slate-200 transition-colors hover:text-gold" data-testid="contact-phone-link">{COMPANY.phone}</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gold/30 bg-gold/10"><Mail className="h-4 w-4 text-gold" /></span>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Email</p>
                      <a href={`mailto:${COMPANY.email}`} className="mt-1 block text-slate-200 transition-colors hover:text-gold" data-testid="contact-email-link">{COMPANY.email}</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gold/30 bg-gold/10"><MapPin className="h-4 w-4 text-gold" /></span>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Factory &amp; Experience Centre</p>
                      <p className="mt-1 text-slate-200" data-testid="contact-address">{COMPANY.address}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gold/30 bg-gold/10"><Clock className="h-4 w-4 text-gold" /></span>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Working Hours</p>
                      <p className="mt-1 text-slate-200">{COMPANY.hours}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <a
                href="https://maps.app.goo.gl/uPytoqyhroGzMAaZ8"
                target="_blank"
                rel="noreferrer"
                className="group relative block overflow-hidden rounded-2xl border border-sky-400/20"
                data-testid="contact-map-card"
              >
                <img src="/images/sohom-map.jpg" alt="SOHOM office location on Google Maps" className="h-56 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-6">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Visit the Experience Centre</p>
                    <p className="mt-2 text-sm text-slate-300">Find us on Bombay Road, Moosapet — coffee on us.</p>
                  </div>
                  <span className="flex items-center gap-1.5 rounded-full bg-gold px-4 py-2 text-xs font-semibold text-obsidian">
                    <MapPin className="h-3.5 w-3.5" /> Open in Maps
                  </span>
                </div>
              </a>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
